import React, { useMemo } from 'react';

import Typography from '@material-ui/core/Typography';

import { formatDate } from '../../lib/helpers/date.helpers';
import { pluralize } from '../../lib/helpers/text.helpers';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';
import { StatusChip } from '../_core/_ui/status-chip.styles';
import { REPORT_CARD_STATUS_COUNTS, REPORT_CARD_STATUS_TONES } from './report-card-access.constants';
import { reportCardStatusLabels } from './report-card-access.helpers';
import { ReportCardAccessMessages, ReportCardAvailabilityMessages } from './report-card-access.types';
import { useReportCardAvailabilityStyles } from './report-card-availability.styles';

interface IReportCardAvailabilitySummaryProps {
  availability: IReportCardAvailability;
  messages: ReportCardAvailabilityMessages;
  statusMessages: ReportCardAccessMessages;
  nouns: { one: string; many: string };
}

export const ReportCardAvailabilitySummary: React.FC<IReportCardAvailabilitySummaryProps> = ({
  availability,
  messages,
  statusMessages,
  nouns,
}) => {
  const classes = useReportCardAvailabilityStyles();
  const labels = useMemo(() => reportCardStatusLabels(statusMessages), [statusMessages]);
  const { counts, releasedAt, revokedAt, includeNonConcluded } = availability;
  const shownCounts = useMemo(() => REPORT_CARD_STATUS_COUNTS.filter(({ field }) => counts[field] > 0), [counts]);
  const players = (count: number) => pluralize(count, nouns.one, nouns.many);
  const waiting = counts.notReleasedConcluded + counts.notReleasedNotConcluded;

  return (
    <>
      <Typography variant="body2" color="textSecondary">
        {releasedAt
          ? <IntlMsg {...messages.msgReleasedOn} values={{ date: formatDate(releasedAt) }} />
          : <IntlMsg {...messages.msgNotReleased} />}
      </Typography>

      {revokedAt && (
        <div className={classes.revokedNotice}>
          <Typography variant="body2"><strong><IntlMsg {...messages.lblAgeGroupRevoked} values={{ date: formatDate(revokedAt) }} /></strong></Typography>
          <Typography variant="body2"><IntlMsg {...messages.msgAgeGroupRevokedExplained} /></Typography>
        </div>
      )}

      {shownCounts.length ? (
        <div className={classes.chips}>
          {shownCounts.map(({ status, field }) => (
            <StatusChip
              key={status}
              size="small"
              $tone={REPORT_CARD_STATUS_TONES[status]}
              label={<IntlMsg {...messages.lblStatusCount} values={{ status: <IntlMsg {...labels[status]} />, count: counts[field] }} />}
            />
          ))}
        </div>
      ) : (
        <Typography variant="body2"><IntlMsg {...messages.msgNoPlayers} /></Typography>
      )}

      {shownCounts.length > 0 && waiting === 0 && (
        <Typography variant="body2"><IntlMsg {...messages.msgNothingWaiting} /></Typography>
      )}
      {counts.notReleasedConcluded > 0 && (
        <Typography variant="body2">
          <IntlMsg {...messages.msgReadyConcluded} values={{ count: counts.notReleasedConcluded, players: players(counts.notReleasedConcluded) }} />
        </Typography>
      )}
      {counts.notReleasedNotConcluded > 0 && (
        <Typography variant="body2">
          <IntlMsg
            {...(includeNonConcluded ? messages.msgNotConcludedIncluded : messages.msgNotConcludedExcluded)}
            values={{
              count: counts.notReleasedNotConcluded,
              players: players(counts.notReleasedNotConcluded),
              haveNot: pluralize(counts.notReleasedNotConcluded, "hasn't", "haven't"),
            }}
          />
        </Typography>
      )}
    </>
  );
};
